// Links de checkout dos produtos Stripe — planos (assinatura) e produtos avulsos.
//
// Antes isto existia só dentro do EditStripePlanModal: um botão "Carregar URLs
// de Checkout" que só então buscava o domínio, e uma lista de caixas azuis com
// a URL crua. Ficou aqui fora por dois motivos: a mesma seção passou a ser
// usada também no modal de produto (curso), e o domínio agora é buscado na
// abertura do modal — o admin não precisa clicar em nada para ver o link.
//
// Cores/tipografia só de ADMIN_COLORS/ADMIN_FONT (ver docs/PADRAO-PAINEL-ADMIN.md).

import React, { useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  Pressable,
  ActivityIndicator,
  Linking,
  StyleSheet,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import AdminButton from "../../../../components/AdminButton";
import { SectionCard, InfoBanner, useResponsive } from "../formStyles";
import { ADMIN_COLORS, ADMIN_FONT } from "../../../../config/theme/adminColors";
import * as productService from "../service/product-service";

export const DOMAIN_HELP_TEXT =
  'Não foi possível determinar o domínio do checkout. Preencha o campo "URL do domínio do Aplicativo" na aba "Minha Conta" e tente novamente.';

// A rota /Cadastro lê estes parâmetros de query (ver pages/SignUp): assinatura
// resolve o preço a partir do productId; produto avulso vai direto no priceId.
export const buildPlanCheckoutUrl = (domain, productId) =>
  domain && productId
    ? `https://${domain}/Cadastro?purchaseType=PLAN&mode=subscription&productId=${productId}`
    : null;

export const buildCourseCheckoutUrl = (domain, course) =>
  domain && course?.priceId
    ? `https://${domain}/Cadastro?purchaseType=COURSE&mode=${course.mode || "payment"}&priceId=${course.priceId}`
    : null;

// Busca o domínio uma vez por abertura do modal. `enabled` evita a leitura no
// Firestore quando a seção nem vai aparecer.
export function useCheckoutDomain(enabled = true) {
  const [domain, setDomain] = useState(null);
  const [loading, setLoading] = useState(!!enabled);
  const [hasError, setHasError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    setLoading(true);
    setHasError(false);

    productService
      .getCurrentDomainFromFirestore()
      .then((value) => {
        if (cancelled) return;
        setDomain(value || null);
        setHasError(!value);
      })
      .catch((error) => {
        if (cancelled) return;
        console.warn("Não foi possível carregar o domínio do checkout:", error);
        setDomain(null);
        setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, attempt]);

  return {
    domain,
    loading,
    hasError,
    reload: () => setAttempt((value) => value + 1),
  };
}

// `navigator.clipboard` é o caminho normal no painel (HTTPS); o textarea é o
// plano B para contextos em que a API não está disponível.
const copyToClipboard = async (text) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (error) {
    console.warn("Clipboard API indisponível, usando fallback:", error);
  }

  try {
    if (typeof document === "undefined") return false;
    const field = document.createElement("textarea");
    field.value = text;
    field.setAttribute("readonly", "");
    field.style.position = "absolute";
    field.style.left = "-9999px";
    document.body.appendChild(field);
    field.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(field);
    return copied;
  } catch (error) {
    console.warn("Falha ao copiar o link:", error);
    return false;
  }
};

const IconAction = ({ icon, label, onPress, highlighted }) => (
  <Pressable
    onPress={onPress}
    accessibilityRole="button"
    accessibilityLabel={label}
    style={({ hovered }) => [
      styles.iconAction,
      hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
      highlighted && {
        backgroundColor: ADMIN_COLORS.successSoft,
        borderColor: ADMIN_COLORS.successBorder,
      },
    ]}
  >
    <Ionicons
      name={icon}
      size={16}
      color={highlighted ? ADMIN_COLORS.success : ADMIN_COLORS.label}
    />
    <Text
      style={[
        styles.iconActionText,
        highlighted && { color: ADMIN_COLORS.success },
      ]}
    >
      {label}
    </Text>
  </Pressable>
);

/**
 * @param {Array<{key: string, label: string, hint?: string, url: string}>} links
 */
export const CheckoutLinksCard = ({
  title = "Links de checkout",
  subtitle,
  links = [],
  loading = false,
  hasError = false,
  onReload,
  emptyMessage = "Nenhum link disponível ainda.",
}) => {
  const { isMobile } = useResponsive();
  const [copiedKey, setCopiedKey] = useState(null);
  const copiedTimer = useRef(null);

  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
    },
    [],
  );

  const flagCopied = (key) => {
    setCopiedKey(key);
    if (copiedTimer.current) clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleCopy = async (key, text) => {
    const copied = await copyToClipboard(text);
    if (copied) flagCopied(key);
  };

  const handleCopyAll = () =>
    handleCopy(
      "__all__",
      links.map((link) => `${link.label}: ${link.url}`).join("\n"),
    );

  const renderBody = () => {
    if (loading) {
      return (
        <View style={styles.loadingRow}>
          <ActivityIndicator size="small" color={ADMIN_COLORS.accent} />
          <Text style={styles.loadingText}>Gerando links…</Text>
        </View>
      );
    }

    if (hasError) {
      return (
        <>
          <InfoBanner variant="warn">{DOMAIN_HELP_TEXT}</InfoBanner>
          {onReload ? (
            <View style={{ alignItems: "flex-start", marginTop: 12 }}>
              <AdminButton
                title="Tentar novamente"
                variant="secondary"
                size="compact"
                icon="refresh-outline"
                onPress={onReload}
              />
            </View>
          ) : null}
        </>
      );
    }

    if (!links.length) {
      return <InfoBanner variant="info">{emptyMessage}</InfoBanner>;
    }

    return (
      <>
        {links.map((link) => (
          <View
            key={link.key}
            style={[
              styles.link,
              // `alignItems: center` herdado da linha faria os filhos se
              // dimensionarem pelo conteúdo ao empilhar — e a URL, que é uma
              // palavra só e longa, vazava para fora do card no celular.
              isMobile && { flexDirection: "column", alignItems: "stretch" },
            ]}
          >
            <View style={[{ minWidth: 0 }, isMobile ? { width: "100%" } : { flex: 1 }]}>
              <View style={styles.linkHead}>
                <Text style={styles.linkLabel}>{link.label}</Text>
                {link.hint ? (
                  <Text style={styles.linkHint}>{link.hint}</Text>
                ) : null}
              </View>
              {/* selectable: dá para marcar e copiar no braço se o clipboard
                  do navegador estiver bloqueado. */}
              <Text style={styles.linkUrl} selectable>
                {link.url}
              </Text>
            </View>

            <View
              style={[
                styles.linkActions,
                isMobile && { alignSelf: "flex-start" },
              ]}
            >
              <IconAction
                icon={copiedKey === link.key ? "checkmark-outline" : "copy-outline"}
                label={copiedKey === link.key ? "Copiado!" : "Copiar"}
                highlighted={copiedKey === link.key}
                onPress={() => handleCopy(link.key, link.url)}
              />
              <IconAction
                icon="open-outline"
                label="Abrir"
                onPress={() => Linking.openURL(link.url)}
              />
            </View>
          </View>
        ))}

        {links.length > 1 ? (
          <View style={{ alignItems: "flex-start", marginTop: 12 }}>
            <AdminButton
              title={
                copiedKey === "__all__"
                  ? "Links copiados!"
                  : "Copiar todos os links"
              }
              variant="secondary"
              size="compact"
              icon={
                copiedKey === "__all__" ? "checkmark-outline" : "copy-outline"
              }
              onPress={handleCopyAll}
            />
          </View>
        ) : null}
      </>
    );
  };

  return (
    <SectionCard title={title} subtitle={subtitle}>
      {renderBody()}
    </SectionCard>
  );
};

const styles = StyleSheet.create({
  loadingRow: { flexDirection: "row", alignItems: "center", gap: 10 },
  loadingText: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
  },
  link: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
    padding: 12,
    marginBottom: 10,
  },
  linkHead: { flexDirection: "row", alignItems: "center", gap: 8 },
  linkLabel: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: "600",
    color: ADMIN_COLORS.ink,
  },
  linkHint: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    color: ADMIN_COLORS.muted,
  },
  linkUrl: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    lineHeight: 17,
    color: ADMIN_COLORS.muted,
    marginTop: 4,
  },
  linkActions: { flexDirection: "row", alignItems: "center", gap: 8 },
  iconAction: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    minHeight: 36,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    backgroundColor: ADMIN_COLORS.surface,
    cursor: "pointer",
  },
  iconActionText: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: "600",
    color: ADMIN_COLORS.label,
  },
});

export default CheckoutLinksCard;
